import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { statusOf } from "@/lib/api";

/**
 * The provider stack.
 *
 * The QueryClient is built PER MOUNT rather than at module scope, so a test can
 * render <App> twice without the second render inheriting the first one's
 * cache. useState(makeQueryClient) - the initialiser form - constructs it once
 * per mount and never on a re-render.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * 401, 403 and 404 are ANSWERS, not transport failures. Retrying a 403
         * cannot turn it into a 200; it only delays the moment the operator is
         * told what happened, three times over.
         */
        retry: (failureCount: number, error: unknown) => {
          const status = statusOf(error);
          if (status !== null && [401, 403, 404].includes(status)) return false;
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export interface AppProps {
  children?: React.ReactNode;
}

export function App({ children }: AppProps): React.ReactElement {
  const [queryClient] = React.useState(makeQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        {children}
        <Toaster richColors position="top-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
